import React, { useContext, useEffect, useMemo, useState } from "react";
import { Content, Wrapper, UploadContainer } from "./style";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { merchantBusinessDetail } from "../../../pages/Dashboard/dashboardSlice";
import { getMerchantDetails } from "../../../pages/Settings/settingsSlice";
import {
  getRequiredDocumentsByType,
  getUploadedDocumentsByBusinessId,
  uploadRequiredDocument,
} from "../../../pages/Compliance/complianceSlice";
import Loader from "../../common/Loader";
import UploadHeader from "../../compliance/Steps/components/UploadInfo";
import SelfCustomUpload from "../../common/CustomUpload/SelfCustomUpload";
import { stringToSentenceCase } from "../../../utils/format";
import { SuccessAlertContext } from "../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { getCountryValue } from "../../compliance/utils";
import { addDirectorDocuments, addRequiredBusinessDocuments, getListOfDirectors } from "../../../pages/Onboarding/redux/actions";
import { updateDirectorsList } from "../../../pages/Onboarding/redux/slice";
import { getDirectorId, getMostRecentUploadedDocs } from "../../../pages/Onboarding/utils/businessDocumentUtils";
import { listOfDirectorsDocuments } from "../../../pages/Onboarding/utils/constants";
import { useRequiredDocs } from "../../../app/hooks/useRequiredDocs";
import { getMerchantBusinessDetails } from "../../../utils/fetchMerchantBusDetails";
import { updateBusinessObj } from "../../../pages/Settings/settingsSlice";

type directorDocPayload = {
  businessId: string;
  name: string;
  id: string;
  poaUrl?: string;
  idUrl?: string;
  photographUrl?: string;
}

type directorDocPayloadArray = directorDocPayload[]

const Verification = () => {
  const dispatch = useAppDispatch();
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );
  const { onShowAlert } = useContext(SuccessAlertContext);
  const [uploadDetails, setUploadDetails] = useState([]);

  const { status: businessStatus, businessId, userId, userObj: { accountUser : { businessCountry } } } = useAppSelector(
    (state) => state.users,
  );
  const { business } = useAppSelector((state) => state.settings);
  const { directorsList, loading } = useAppSelector((state) => state.onboarding)

  const businessTypeId = business?.businessType
  const { requiredDocs } = useRequiredDocs({ businessTypeId, businessCountry })

  const {
    status: docStatus,
    documents,
    uploadedDocuments,
  } = useAppSelector((state) => state.compliance);

  useEffect(() => {
    if (!businessId) {
      getMerchantBusinessDetails(dispatch, userId, updateBusinessObj)
    }
  }, [business, userId])

  useEffect(() => {
    if (businessId) {
      if (Object.keys(business).length < 1) {
        dispatch(getMerchantDetails(userId))
      }
      dispatch(getUploadedDocumentsByBusinessId({ businessId }));
    }

    const fetchListOfDirectors = async () => {
      const directorList = await dispatch(getListOfDirectors(businessId))
      
      if (getListOfDirectors.fulfilled.match(directorList)) {
        dispatch(updateDirectorsList(directorList.payload))
      }
    }
    fetchListOfDirectors()
  }, [businessId, userId]);

  useEffect(() => {
    if (business && Object.keys(business).length) {
      dispatch(
        getRequiredDocumentsByType({
          businessType: business.businessType,
          country: getCountryValue(business.country),
        }),
      );
    }
  }, [business, dispatch]);

  const uploadDocumentURL = async (downUrl, busDocId, busDocName, directorName, approvalStatus) => {
      if (directorName === null || approvalStatus === "NOT UPLOADED") {
        const payload = {
          businessDocument: busDocId,
          url: downUrl,
          approved: false,
          business: businessId,
          documentApprovalStatus: "PENDING",
        };
          const uploadBusinessDocsAction = await dispatch(uploadRequiredDocument(payload));
          if (uploadRequiredDocument.fulfilled.match(uploadBusinessDocsAction)) {
            dispatch(getUploadedDocumentsByBusinessId({ businessId }))
            onShowAlert("The business document has been updated successfully");
          }
       return
      }

    const directorIdToEdit = getDirectorId(directorName, directorsList)

    const directorDocPayload: directorDocPayloadArray = [{
      businessId: businessId,
      name: directorName,
      id: directorIdToEdit,
    }]

    const directorDocsMap = {
      photographId: 'photographUrl',
      identityId: 'idUrl',
      proofOfAddressId: 'poaUrl'
    }

    const document = listOfDirectorsDocuments.find(doc => 
      doc.photographId === busDocName || 
      doc.identityId === busDocName || 
      doc.proofOfAddressId === busDocName
    )

    if (document) {
      const urlField = Object.entries(directorDocsMap).find(([idField]) => 
        document[idField] === busDocName
      )?.[1]

      if (urlField) {
        directorDocPayload[0][urlField] = downUrl
      }
    }

    const directorDocsAction = await dispatch(addDirectorDocuments(directorDocPayload))

    if (addDirectorDocuments.fulfilled.match(directorDocsAction)) {
      dispatch(getUploadedDocumentsByBusinessId({ businessId }))
      onShowAlert("The director document has been updated successfully");
    }
  };

  const onUploadDocumentDone = async (newDoc: any) => {
    const newDetails = [...uploadDetails];
    const detailsIndex = uploadDetails.findIndex((det) => det.id === newDoc.id);
    if (detailsIndex !== -1) {
      newDetails.splice(detailsIndex, 1, newDoc);
      setUploadDetails(newDetails);
    }
  };

  const mostRecentUploadedDocs = useMemo(() => {
    return getMostRecentUploadedDocs(uploadedDocuments, requiredDocs)
  }, [uploadedDocuments])

  return (
    <Wrapper>
      <Loader isLoading={businessStatus === "loading" || docStatus === "loading" || loading} />

      <Content>
        {mostRecentUploadedDocs?.map((busType) => {
          return ["document", "upload", "website"].includes(
            busType.businessDocument.type,
          ) ? (
            <UploadContainer key={busType.businessDocument.id+busType.directorName}>
              <UploadHeader
                label={busType.businessDocument.description}
                title={
                  busType.businessDocument.document
                    ? stringToSentenceCase(busType.businessDocument.document + (busType?.directorName ? ` (${busType?.directorName})` : ''))
                    : ""
                }
              />
              <SelfCustomUpload
                onUpload={onUploadDocumentDone}
                docId={busType.businessDocument.id}
                docName={busType.businessDocument.document}
                approvalStatus={busType?.documentApprovalStatus}
                uploadPath={`verification/documents/${busType.businessDocument.id}/`}
                onUploadFinish={uploadDocumentURL}
                directorName={busType?.directorName}
              />
            </UploadContainer>
          ) : null;
        })}
      </Content>
    </Wrapper>
  );
};

export default Verification;
